import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Boxes,
  CircleCheck,
  FolderTree,
  Plus,
  Sparkles,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  linkServiceInventory,
  patchServiceInventoryLink,
  unlinkServiceInventory,
  useInventory,
  useInventoryCategories,
  useManagedServiceInventory,
  type InventoryItem,
  type InventoryLinkRole,
  type LinkInventoryBody,
  type ManagedInventoryLink,
} from "@/lib/inventory";
import { ConfirmDialog } from "@/components/v2/ConfirmDialog";
import { EmptyState } from "@/components/v2/EmptyState";
import { useToast } from "@/components/v2/Toast";

/**
 * Per-service Inventory tab. Two grouped sections:
 *   * **Tier options** — `plan`-kind items the customer picks ONE of when
 *     booking. Each row is either an individual `plan` item OR a
 *     category-link (live binding: items added to that category later
 *     auto-include unless excluded).
 *   * **Add-ons**       — `physical`-kind items the customer can opt into
 *     per booking. Same item-or-category row model.
 *
 * Pencil `c6N5i` (`v2/Service — Inventory`).
 */
export function ServiceInventory() {
  const { id } = useParams<{ id: string }>();
  const links = useManagedServiceInventory(id);
  const all = links.data ?? [];
  const tiers = all.filter((l) => l.role === "tier");
  const addons = all.filter((l) => l.role === "addon");

  return (
    <div className="space-y-6">
      <Section
        title="Tier options"
        description="Customers pick ONE tier when booking — its price drives the booking total."
        kind="tier"
        links={tiers}
        serviceId={id!}
      />
      <Section
        title="Add-ons"
        description="Optional extras the customer can add per booking. Each adds to the price + decrements stock."
        kind="addon"
        links={addons}
        serviceId={id!}
      />
    </div>
  );
}

function Section({
  title,
  description,
  kind,
  links,
  serviceId,
}: {
  title: string;
  description: string;
  kind: InventoryLinkRole;
  links: ManagedInventoryLink[];
  serviceId: string;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<ManagedInventoryLink | null>(null);
  const [editing, setEditing] = useState<ManagedInventoryLink | null>(null);
  const qc = useQueryClient();
  const toast = useToast();

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["service.inventory", serviceId] });
    qc.invalidateQueries({ queryKey: ["service.inventory.managed", serviceId] });
  };

  const onErr = (e: unknown) => toast.error("Couldn't save", (e as Error).message);

  const link = useMutation({
    mutationFn: (body: LinkInventoryBody) => linkServiceInventory(serviceId, body),
    onSuccess: () => {
      invalidate();
      setPickerOpen(false);
      toast.success(kind === "tier" ? "Tier added" : "Add-on linked");
    },
    onError: (e) => {
      onErr(e);
      setPickerOpen(false);
    },
  });

  const toggleRequired = useMutation({
    mutationFn: (l: ManagedInventoryLink) =>
      patchServiceInventoryLink(serviceId, l.linkId, { required: !l.required }),
    onSuccess: invalidate,
    onError: onErr,
  });

  const remove = useMutation({
    mutationFn: (l: ManagedInventoryLink) => unlinkServiceInventory(serviceId, l.linkId),
    onSuccess: () => {
      invalidate();
      setPendingDelete(null);
      toast.success("Removed");
    },
    onError: (e) => {
      onErr(e);
      setPendingDelete(null);
    },
  });

  const Icon = kind === "tier" ? Sparkles : Boxes;

  return (
    <section className="rounded-lg border bg-card">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold tracking-tight">{title}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
        </div>
        <Button size="sm" onClick={() => setPickerOpen(true)}>
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          {kind === "tier" ? "Add tier" : "Add add-on"}
        </Button>
      </div>

      {links.length === 0 ? (
        <EmptyState
          bare
          icon={Icon}
          title={kind === "tier" ? "No tiers yet" : "No add-ons yet"}
          description={
            kind === "tier"
              ? "Link Plan items individually or by category so customers can pick a price tier when booking."
              : "Link Physical items individually or by category so customers can opt into add-ons per booking."
          }
        />
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/40 border-b border-border text-left">
              <th className="px-4 py-3 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                Linked
              </th>
              <th className="px-4 py-3 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                Type
              </th>
              <th className="px-4 py-3 text-[11px] font-medium uppercase tracking-wider text-muted-foreground text-right">
                {kind === "tier" ? "Price" : "Stock"}
              </th>
              <th className="px-4 py-3 text-[11px] font-medium uppercase tracking-wider text-muted-foreground text-right">
                {kind === "tier" ? "Required" : "Status"}
              </th>
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {links.map((l) =>
              l.subjectKind === "item" ? (
                <ItemLinkRow
                  key={l.linkId}
                  link={l}
                  kind={kind}
                  onToggleRequired={() => toggleRequired.mutate(l)}
                  onDelete={() => setPendingDelete(l)}
                />
              ) : (
                <CategoryLinkRow
                  key={l.linkId}
                  link={l}
                  kind={kind}
                  onToggleRequired={() => toggleRequired.mutate(l)}
                  onEdit={() => setEditing(l)}
                  onDelete={() => setPendingDelete(l)}
                />
              ),
            )}
          </tbody>
        </table>
      )}

      <ItemPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        kind={kind}
        excludeItemIds={links
          .filter((l): l is Extract<ManagedInventoryLink, { subjectKind: "item" }> =>
            l.subjectKind === "item",
          )
          .map((l) => l.item.id)}
        excludeCategories={links
          .filter(
            (l): l is Extract<ManagedInventoryLink, { subjectKind: "category" }> =>
              l.subjectKind === "category",
          )
          .map((l) => l.category)}
        onSubmit={(body) => link.mutate(body)}
        pending={link.isPending}
      />

      <CategoryLinkEditDialog
        link={editing}
        onClose={() => setEditing(null)}
        serviceId={serviceId}
        onSaved={() => {
          invalidate();
          setEditing(null);
          toast.success("Saved");
        }}
        onError={onErr}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete)}
        title={
          pendingDelete?.subjectKind === "category"
            ? `Unlink category "${pendingDelete?.category}"?`
            : `Remove "${pendingDelete?.subjectKind === "item" ? pendingDelete.item.name : ""}"?`
        }
        description={
          pendingDelete?.subjectKind === "category"
            ? "Unlinks this whole category. Existing bookings keep their snapshot of name + price."
            : "This unlinks the item from this service. Existing bookings keep their snapshot of name + price."
        }
        confirmLabel={pendingDelete?.subjectKind === "category" ? "Unlink category" : "Remove"}
        pending={remove.isPending}
      />
    </section>
  );
}

function ItemLinkRow({
  link,
  kind,
  onToggleRequired,
  onDelete,
}: {
  link: Extract<ManagedInventoryLink, { subjectKind: "item" }>;
  kind: InventoryLinkRole;
  onToggleRequired: () => void;
  onDelete: () => void;
}) {
  const Icon = kind === "tier" ? Sparkles : Boxes;
  const item = link.item;
  return (
    <tr className="border-b border-border last:border-0 group">
      <td className="px-4 py-3">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
          <div className="min-w-0">
            <p className="font-medium text-sm truncate">{item.name}</p>
            <p className="text-xs text-muted-foreground mt-0.5 truncate">
              {item.category ?? "—"}
            </p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-foreground/70">
          Item
        </span>
      </td>
      <td className="px-4 py-3 text-right">
        {kind === "tier" ? (
          <span className="text-sm font-semibold tabular-nums">
            {item.currency ?? ""}{" "}
            {item.unitPrice != null
              ? Number(item.unitPrice).toLocaleString()
              : "—"}
          </span>
        ) : (
          <span
            className={`text-sm tabular-nums ${
              item.qtyOnHand === 0 ? "text-status-cancelled font-medium" : ""
            }`}
          >
            {item.qtyOnHand}
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        {kind === "tier" ? (
          <Toggle
            checked={link.required}
            onChange={onToggleRequired}
            ariaLabel={`Mark ${item.name} as required`}
          />
        ) : item.qtyOnHand > 0 ? (
          <span className="inline-flex items-center gap-1 text-xs text-status-completed">
            <CircleCheck className="h-3 w-3" /> In stock
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs text-status-cancelled">
            <AlertTriangle className="h-3 w-3" /> Out of stock
          </span>
        )}
      </td>
      <td className="px-2 text-right">
        <button
          type="button"
          onClick={onDelete}
          className="opacity-0 group-hover:opacity-100 h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition"
          aria-label={`Remove ${item.name}`}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </td>
    </tr>
  );
}

function CategoryLinkRow({
  link,
  kind,
  onToggleRequired,
  onEdit,
  onDelete,
}: {
  link: Extract<ManagedInventoryLink, { subjectKind: "category" }>;
  kind: InventoryLinkRole;
  onToggleRequired: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const includedCount = link.previewItems.length - link.excludedItemIds.length;
  return (
    <tr
      className="border-b border-border last:border-0 group cursor-pointer hover:bg-muted/30"
      onClick={onEdit}
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-2 min-w-0">
          <FolderTree className="h-4 w-4 text-primary shrink-0" />
          <div className="min-w-0">
            <p className="font-medium text-sm truncate">{link.category}</p>
            <p className="text-xs text-muted-foreground mt-0.5 truncate">
              {includedCount} of {link.previewItems.length} item
              {link.previewItems.length === 1 ? "" : "s"} included
            </p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
          <FolderTree className="h-2.5 w-2.5" /> Category
        </span>
      </td>
      <td className="px-4 py-3 text-right text-xs text-muted-foreground italic">
        {link.excludedItemIds.length > 0
          ? `${link.excludedItemIds.length} excluded`
          : "All included"}
      </td>
      <td className="px-4 py-3 text-right">
        {kind === "tier" ? (
          <Toggle
            checked={link.required}
            onChange={(e) => {
              (e as unknown as { stopPropagation?: () => void }).stopPropagation?.();
              onToggleRequired();
            }}
            ariaLabel={`Mark category ${link.category} as required`}
          />
        ) : (
          <span className="text-xs text-muted-foreground italic">live</span>
        )}
      </td>
      <td className="px-2 text-right">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="opacity-0 group-hover:opacity-100 h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition"
          aria-label={`Unlink category ${link.category}`}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </td>
    </tr>
  );
}

/**
 * Pick-an-item OR pick-a-category dialog. Mode toggle at top; bottom shows
 * either the item picker (today's UX) or a category dropdown + preview-with-
 * exclusions checklist.
 */
function ItemPickerDialog({
  open,
  onClose,
  kind,
  excludeItemIds,
  excludeCategories,
  onSubmit,
  pending,
}: {
  open: boolean;
  onClose: () => void;
  kind: InventoryLinkRole;
  excludeItemIds: string[];
  excludeCategories: string[];
  onSubmit: (body: LinkInventoryBody) => void;
  pending: boolean;
}) {
  // Kind ⇄ role is no longer enforced — operators can link any inventory
  // item as a tier or as an add-on. Stock semantics still come from each
  // item's kind on the booking write path (plans never decrement stock).
  const inventory = useInventory();
  const categories = useInventoryCategories();
  const [mode, setMode] = useState<"item" | "category">("item");
  const [pickedCategory, setPickedCategory] = useState<string>("");
  const [excludedInPreview, setExcludedInPreview] = useState<Set<string>>(new Set());

  // Reset all picker state every time the dialog re-opens.
  useEffect(() => {
    if (open) {
      setMode("item");
      setPickedCategory("");
      setExcludedInPreview(new Set());
    }
  }, [open]);

  const candidates = useMemo(() => {
    const items = (inventory.data?.items ?? []) as InventoryItem[];
    return items.filter((i) => !excludeItemIds.includes(i.id));
  }, [inventory.data, excludeItemIds]);

  const categoryOptions = useMemo(() => {
    const list = categories.data ?? [];
    return list.filter((c) => !excludeCategories.includes(c));
  }, [categories.data, excludeCategories]);

  const previewItems = useMemo(() => {
    if (!pickedCategory) return [] as InventoryItem[];
    const items = (inventory.data?.items ?? []) as InventoryItem[];
    return items.filter((i) => (i.category ?? "") === pickedCategory);
  }, [inventory.data, pickedCategory]);

  if (!open) return null;

  const submitCategory = () => {
    if (!pickedCategory) return;
    onSubmit({
      subjectKind: "category",
      category: pickedCategory,
      role: kind,
      excludeItemIds: Array.from(excludedInPreview),
    });
  };

  const toggleExcluded = (id: string) => {
    setExcludedInPreview((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-foreground/40" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg rounded-lg border bg-popover shadow-2xl overflow-hidden"
      >
        <div className="px-5 py-4 border-b border-border">
          <p className="text-sm font-semibold tracking-tight">
            {kind === "tier" ? "Add a tier" : "Add an add-on"}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Pick a single inventory item, or link a whole category at once.
          </p>
        </div>

        <div className="px-5 pt-3">
          <div className="inline-flex rounded-md border bg-card p-0.5">
            <button
              type="button"
              onClick={() => setMode("item")}
              className={`px-3 py-1 text-xs font-medium rounded ${
                mode === "item" ? "bg-primary text-primary-foreground" : "text-muted-foreground"
              }`}
            >
              Pick item
            </button>
            <button
              type="button"
              onClick={() => setMode("category")}
              className={`px-3 py-1 text-xs font-medium rounded inline-flex items-center gap-1 ${
                mode === "category"
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground"
              }`}
            >
              <FolderTree className="h-3 w-3" /> Pick category
            </button>
          </div>
        </div>

        {mode === "item" ? (
          <div className="max-h-[60vh] overflow-y-auto mt-2">
            {inventory.isLoading && (
              <p className="px-5 py-6 text-sm text-muted-foreground">Loading…</p>
            )}
            {!inventory.isLoading && candidates.length === 0 && (
              <div className="px-5 py-6 text-sm text-muted-foreground">
                No more inventory items to link.{" "}
                <a href="/inventory" className="text-primary hover:underline">
                  Add some in Inventory →
                </a>
              </div>
            )}
            <ul className="divide-y">
              {candidates.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    disabled={pending}
                    onClick={() =>
                      onSubmit({
                        subjectKind: "item",
                        itemId: item.id,
                        role: kind,
                      })
                    }
                    className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-muted/40 disabled:opacity-50"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <p className="font-medium text-sm truncate">{item.name}</p>
                        <KindPill kind={item.kind} />
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">
                        {item.category ?? "—"}
                      </p>
                    </div>
                    {item.unitPrice != null && (
                      <span className="text-sm font-semibold tabular-nums shrink-0">
                        {item.currency ?? ""}{" "}
                        {Number(item.unitPrice).toLocaleString()}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="px-5 pt-3 pb-4 space-y-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">
                Category
              </label>
              <Select
                value={pickedCategory || undefined}
                onValueChange={(v) => {
                  setPickedCategory(v);
                  setExcludedInPreview(new Set());
                }}
              >
                <SelectTrigger className="mt-1 h-10">
                  <SelectValue placeholder="Choose a category…" />
                </SelectTrigger>
                <SelectContent>
                  {categoryOptions.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {categoryOptions.length === 0 && !categories.isLoading && (
                <p className="text-xs text-muted-foreground mt-1">
                  No more categories to link.{" "}
                  <a href="/inventory" className="text-primary hover:underline">
                    Add items in Inventory →
                  </a>
                </p>
              )}
            </div>

            {pickedCategory && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">
                  Items in this category — uncheck to exclude
                </p>
                {previewItems.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">
                    This category is empty.
                  </p>
                ) : (
                  <ul className="rounded-md border divide-y max-h-[40vh] overflow-y-auto">
                    {previewItems.map((it) => {
                      const excluded = excludedInPreview.has(it.id);
                      return (
                        <li key={it.id}>
                          <label className="flex items-center gap-3 px-3 py-2 hover:bg-muted/30 cursor-pointer">
                            <Checkbox
                              checked={!excluded}
                              onCheckedChange={() => toggleExcluded(it.id)}
                              aria-label={`Include ${it.name}`}
                            />
                            <span className="flex-1 min-w-0">
                              <span className="flex items-center gap-2 min-w-0">
                                <span className="text-sm truncate">{it.name}</span>
                                <KindPill kind={it.kind} />
                              </span>
                              {it.unitPrice != null && (
                                <span className="block text-xs text-muted-foreground tabular-nums">
                                  {it.currency ?? ""}{" "}
                                  {Number(it.unitPrice).toLocaleString()}
                                </span>
                              )}
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
                <p className="text-xs text-muted-foreground italic mt-2">
                  Live binding — items added to this category later auto-include
                  unless explicitly excluded.
                </p>
              </div>
            )}
          </div>
        )}

        <div className="px-5 py-3 border-t border-border flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          {mode === "category" && (
            <Button
              size="sm"
              disabled={!pickedCategory || pending || previewItems.length === 0}
              onClick={submitCategory}
            >
              {pending ? "Linking…" : "Link category"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function CategoryLinkEditDialog({
  link,
  onClose,
  serviceId,
  onSaved,
  onError,
}: {
  link: ManagedInventoryLink | null;
  onClose: () => void;
  serviceId: string;
  onSaved: () => void;
  onError: (e: unknown) => void;
}) {
  const [excluded, setExcluded] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (link?.subjectKind === "category") {
      setExcluded(new Set(link.excludedItemIds));
    }
  }, [link]);

  const save = useMutation({
    mutationFn: () =>
      patchServiceInventoryLink(serviceId, link!.linkId, {
        excludeItemIds: Array.from(excluded),
      }),
    onSuccess: onSaved,
    onError,
  });

  if (!link || link.subjectKind !== "category") return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-foreground/40" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg rounded-lg border bg-popover shadow-2xl overflow-hidden"
      >
        <div className="px-5 py-4 border-b border-border">
          <p className="text-sm font-semibold tracking-tight inline-flex items-center gap-2">
            <FolderTree className="h-4 w-4 text-primary" /> {link.category}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Toggle items to include/exclude from this category link.
          </p>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {link.previewItems.length === 0 ? (
            <p className="px-5 py-6 text-sm text-muted-foreground">
              This category has no items right now.
            </p>
          ) : (
            <ul className="divide-y">
              {link.previewItems.map((it) => {
                const isExcluded = excluded.has(it.id);
                return (
                  <li key={it.id}>
                    <label className="flex items-center gap-3 px-5 py-3 hover:bg-muted/30 cursor-pointer">
                      <Checkbox
                        checked={!isExcluded}
                        onCheckedChange={() => {
                          setExcluded((prev) => {
                            const next = new Set(prev);
                            if (next.has(it.id)) next.delete(it.id);
                            else next.add(it.id);
                            return next;
                          });
                        }}
                        aria-label={`Include ${it.name}`}
                      />
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm truncate">{it.name}</span>
                        {it.unitPrice != null && (
                          <span className="block text-xs text-muted-foreground tabular-nums">
                            {it.currency ?? ""}{" "}
                            {Number(it.unitPrice).toLocaleString()}
                          </span>
                        )}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="px-5 py-3 border-t border-border flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function KindPill({ kind }: { kind: string }) {
  const Icon = kind === "plan" ? Sparkles : Boxes;
  const label =
    kind === "plan"
      ? "Plan"
      : kind === "rental"
        ? "Rental"
        : kind === "addon"
          ? "Add-on"
          : "Physical";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium shrink-0 ${
        kind === "plan"
          ? "bg-primary/10 text-primary"
          : "bg-muted text-foreground/70"
      }`}
    >
      <Icon className="h-2.5 w-2.5" />
      {label}
    </span>
  );
}

function Toggle({
  checked,
  onChange,
  ariaLabel,
}: {
  checked: boolean;
  onChange: (e?: React.MouseEvent) => void;
  ariaLabel: string;
}) {
  // The Inventory `Toggle` callers pass an *event handler* (not a value
  // handler) so they can `stopPropagation()` on row-click parents. Map
  // that to Radix Switch's onClick — the parent flips state itself, and
  // the controlled `checked` prop drives the visual update.
  return (
    <Switch
      checked={checked}
      aria-label={ariaLabel}
      onClick={(e) => onChange(e)}
    />
  );
}

